"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Globe, ImageIcon, Loader2, RefreshCw, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { FadeImage } from "@/components/ui/fade-image";
import { springSnappy } from "@/lib/animations";
import { parseIsoDate } from "@/lib/calendar/week";
import { cn } from "@/lib/utils";

export interface SuggestedMeal {
  recipeId: string;
  date: string;
  isOwned: boolean;
  recipe: { title: string; image: string | null };
}

interface SuggestedDayListProps<M extends SuggestedMeal> {
  /** The days to show, in order, each with its meals (a day may have none). */
  days: { iso: string; meals: M[] }[];
  todayIso: string;
  locale: string;
  /** The day whose meals are being replaced, if any. */
  swappingIso: string | null;
  swapDisabled?: boolean;
  onSwap: (iso: string) => void;
  onRemove: (meal: M) => void;
}

/**
 * Suggested meals as a vertical list of days (Jeremy, 2026-10-10), shared by the Dashboard's weekly draft and the
 * calendar's suggest sheet so suggestions look the same everywhere: the day and its swap button at the start, then
 * one row per meal with a thumbnail and its own remove button. No card border; thin lines part the days. The 64 px
 * thumbnail makes a row as tall as the day column, so a one-meal day has the same space above and below.
 */
export function SuggestedDayList<M extends SuggestedMeal>({
  days,
  todayIso,
  locale,
  swappingIso,
  swapDisabled = false,
  onSwap,
  onRemove,
}: SuggestedDayListProps<M>) {
  const t = useTranslations("dashboard");
  const tc = useTranslations("calendar");
  const weekdayFmt = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" });

  return (
    <ol className="divide-y divide-border">
      {days.map(({ iso, meals }) => {
        const swapping = swappingIso === iso;
        return (
          <li key={iso} className="flex gap-3 py-1">
            <DayColumn iso={iso} isToday={iso === todayIso} weekdayFmt={weekdayFmt}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onSwap(iso)}
                disabled={swapping || swapDisabled}
                aria-label={t("draftSwap")}
                className="size-7 text-muted-foreground"
              >
                {swapping ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                  <RefreshCw className="size-4" aria-hidden />
                )}
              </Button>
            </DayColumn>
            {meals.length === 0 ? (
              <p className="flex-1 py-4 text-sm text-muted-foreground">{tc("noMealPlanned")}</p>
            ) : (
              <ul className="min-w-0 flex-1 divide-y divide-border">
                <AnimatePresence mode="popLayout" initial={false}>
                  {meals.map((meal) => (
                    <motion.li
                      key={`${meal.recipeId}-${meal.date}`}
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={springSnappy}
                      className="flex items-center gap-3 py-2"
                    >
                      <MealThumb meal={meal} />
                      <span className="line-clamp-2 min-w-0 flex-1 leading-snug">
                        {meal.recipe.title}
                      </span>
                      <button
                        type="button"
                        onClick={() => onRemove(meal)}
                        aria-label={tc("removeSuggestion")}
                        className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:text-foreground"
                      >
                        <X className="size-4" aria-hidden />
                      </button>
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** The list's shape while suggestions load: each day with a placeholder row. */
export function SuggestedDayListSkeleton({
  days,
  todayIso,
  locale,
}: {
  days: string[];
  todayIso: string;
  locale: string;
}) {
  const weekdayFmt = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" });
  return (
    <ol className="divide-y divide-border" aria-hidden>
      {days.map((iso) => (
        <li key={iso} className="flex gap-3 py-1">
          <DayColumn iso={iso} isToday={iso === todayIso} weekdayFmt={weekdayFmt}>
            <div className="size-7" />
          </DayColumn>
          <div className="flex flex-1 items-center gap-3 py-2">
            <div className="size-16 shrink-0 animate-pulse rounded-lg bg-muted" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
          </div>
        </li>
      ))}
    </ol>
  );
}

function DayColumn({
  iso,
  isToday,
  weekdayFmt,
  children,
}: {
  iso: string;
  isToday: boolean;
  weekdayFmt: Intl.DateTimeFormat;
  children: React.ReactNode;
}) {
  const date = parseIsoDate(iso)!;
  return (
    <div
      className={cn(
        "flex w-11 shrink-0 flex-col items-center pt-2",
        isToday ? "text-primary" : "text-foreground"
      )}
    >
      <span className="text-xs font-medium uppercase">{weekdayFmt.format(date)}</span>
      <span className="text-xl font-semibold leading-tight">{date.getUTCDate()}</span>
      {children}
    </div>
  );
}

/** A meal's square thumbnail: the photo, or the no-photo placeholder when there is none or it fails to load; a globe
 * marks a recipe the user does not own. */
function MealThumb({ meal }: { meal: SuggestedMeal }) {
  const [failed, setFailed] = useState(false);
  const image = failed ? null : meal.recipe.image;
  return (
    <div className="relative size-16 shrink-0 overflow-hidden rounded-lg bg-muted">
      {image ? (
        <FadeImage
          src={image}
          alt=""
          fill
          sizes="64px"
          className="object-cover"
          // The list can be the whole page, so a thumbnail in view can be its largest paint.
          priority
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
          <ImageIcon className="size-5 opacity-30" strokeWidth={1.5} aria-hidden />
        </div>
      )}
      {!meal.isOwned && (
        <div className="absolute start-1 top-1 flex size-4 items-center justify-center rounded-full bg-black/60 text-white">
          <Globe className="size-2.5" strokeWidth={2} aria-hidden />
        </div>
      )}
    </div>
  );
}
